import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Badge } from '@/components/ui/Badge';
import {
  DropdownMenuRoot,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu/dropdown-menu';
import { Input } from '@/components/ui/Input';
import { XStack } from '@/components/ui/layout/layout';
import { afterFrames } from '@/lib/animate';
import { cn } from '@/lib/utils';

export type MultiSelectOption = {
  value: string;
  label: string;
};

type MultiSelectSearchableDropdownProps = {
  options: MultiSelectOption[];
  selected: MultiSelectOption[];
  onSelect: (value: string) => void;
  onRemove: (value: string) => void;
  onClearAll: () => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyResult?: ReactNode;
  triggerClassName?: string;
  contentClassName?: string;
};

export const MultiSelectSearchableDropdown = ({
  options,
  selected,
  onSelect,
  onRemove,
  onClearAll,
  placeholder = 'Select options...',
  searchPlaceholder = 'Search...',
  emptyResult = 'No options found',
  triggerClassName,
  contentClassName,
}: MultiSelectSearchableDropdownProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;

    const searchLower = searchTerm.toLowerCase();
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(searchLower) ||
        option.value.toLowerCase().includes(searchLower)
    );
  }, [searchTerm, options]);

  const handleSelect = (value: string) => {
    onSelect(value);
    setSearchTerm('');
    setHighlightedIndex(-1);
    afterFrames(2, () => {
      searchInputRef.current?.focus();
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!filteredOptions.length) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (
          highlightedIndex >= 0 &&
          highlightedIndex < filteredOptions.length
        ) {
          handleSelect(filteredOptions[highlightedIndex].value);
        }
        break;
      case 'Escape':
        setIsDropdownOpen(false);
        break;
      default:
        if (e.key.length === 1) {
          afterFrames(2, () => {
            searchInputRef.current?.focus();
          });
        }
        break;
    }
    e.stopPropagation();
  };

  const handleDropdownOpenChange = (open: boolean) => {
    setIsDropdownOpen(open);
    setSearchTerm('');
    setHighlightedIndex(-1);
    if (open) {
      afterFrames(2, () => {
        searchInputRef.current?.focus();
      });
    }
  };

  useEffect(() => {
    if (highlightedIndex >= 0 && scrollContainerRef.current) {
      const items = scrollContainerRef.current.querySelectorAll(
        '[data-dropdown-item]'
      );
      const highlightedItem = items[highlightedIndex] as
        | HTMLElement
        | undefined;
      highlightedItem?.scrollIntoView({
        behavior: 'instant',
        block: 'nearest',
        inline: 'nearest',
      });
    }
  }, [highlightedIndex]);

  return (
    <DropdownMenuRoot
      open={isDropdownOpen}
      onOpenChange={handleDropdownOpenChange}
    >
      <DropdownMenuTrigger asChild>
        <XStack
          wrap
          role="combobox"
          aria-expanded={isDropdownOpen}
          tabIndex={0}
          className={cn(
            'w-full min-h-9 items-center gap-1 rounded-md border border-border-strong px-2 py-1.5 focus-ring',
            triggerClassName
          )}
        >
          {selected.length === 0 ? (
            <span className="px-1 text-sm text-foreground-muted">
              {placeholder}
            </span>
          ) : (
            selected.map((option) => (
              <Badge
                key={option.value}
                variant="secondary"
                className="gap-1 pr-0.5"
              >
                {option.label}
                <button
                  type="button"
                  aria-label={`Remove ${option.label}`}
                  className="rounded-sm p-0.5 text-foreground-muted hover:text-foreground focus-ring"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove(option.value);
                  }}
                >
                  <Icons.cancel size={12} />
                </button>
              </Badge>
            ))
          )}
          <XStack className="ml-auto items-center gap-1 pl-1">
            {selected.length > 0 && (
              <button
                type="button"
                aria-label="Clear all"
                className="rounded-sm p-0.5 text-foreground-muted hover:text-foreground focus-ring"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  onClearAll();
                }}
              >
                <Icons.cancel size={14} />
              </button>
            )}
            <Icons.chevronDown
              size={14}
              className={cn(
                'text-foreground-muted transition-transform',
                isDropdownOpen && 'rotate-180'
              )}
            />
          </XStack>
        </XStack>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className={cn(
          'w-[var(--radix-dropdown-menu-trigger-width)] p-0 overflow-hidden',
          contentClassName
        )}
        autoFocus={false}
        onCloseAutoFocus={(e) => e.preventDefault()}
        onKeyDown={handleKeyDown}
      >
        <XStack className="relative p-2">
          <Icons.search
            size={14}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-foreground-muted"
          />
          <Input
            ref={searchInputRef}
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setHighlightedIndex(-1);
            }}
            className="h-7 pl-7 text-xs"
            onKeyDown={handleKeyDown}
          />
        </XStack>
        <div
          ref={scrollContainerRef}
          className="max-h-60 overflow-y-auto p-1 pt-0"
          onWheel={(e) => e.stopPropagation()}
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <DropdownMenuItem
                key={option.value}
                data-dropdown-item
                textSize="xs"
                onPointerLeave={(event) => event.preventDefault()}
                onPointerMove={(event) => event.preventDefault()}
                onSelect={(event) => event.preventDefault()}
                onClick={() => handleSelect(option.value)}
                onMouseEnter={() => setHighlightedIndex(index)}
                className={cn(
                  'py-1.5 mb-1 scroll-my-1 transition-none hover:bg-transparent',
                  highlightedIndex === index &&
                    'bg-surface-active hover:bg-surface-active'
                )}
              >
                {option.label}
              </DropdownMenuItem>
            ))
          ) : (
            <div className="px-2 py-3 text-center text-xs text-foreground-muted">
              {emptyResult}
            </div>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenuRoot>
  );
};
